import React from "react";
import { Modal } from "react-bootstrap";
import { Users, Film, Briefcase, TrendingUp } from "lucide-react";

const FollowedCastingDirectorsModal = ({
  showFollowingModal,
  setShowFollowingModal,
  dashboardData,
  navigateToCDProfile,
  handleModalFollowToggle,
  formatCamelCase
}) => {
  return (
    <Modal show={showFollowingModal} onHide={() => setShowFollowingModal(false)} centered dialogClassName="cd-following-modal-dialog" contentClassName="bg-dark text-white border border-secondary rounded-4 shadow-lg">
      <Modal.Header closeButton closeVariant="white" className="border-secondary px-3 py-2.5" style={{ background: '#111827' }}>
        <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2 text-white">
          <Users size={18} className="text-primary" /> Followed Casting Directors
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="cd-modal-body px-3 py-2.5" style={{ maxHeight: '420px', overflowY: 'auto', background: '#0b0f19' }}>
        {dashboardData?.followedCDs?.length > 0 ? (
          <div className="d-flex flex-column gap-2.5">
            {dashboardData.followedCDs.map((cd) => (
              <div key={cd.id} className="p-3 rounded-3 d-flex flex-column gap-3 cd-modal-card" style={{ background: '#141824', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}>
                
                {/* TOP ROW: AVATAR + NAME + BADGE + AGENCY + UNFOLLOW BUTTON */}
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold bg-danger text-white overflow-hidden flex-shrink-0 cd-modal-avatar" 
                      style={{ width: '44px', height: '44px', border: '2px solid #E10600', fontSize: '0.95rem', cursor: 'pointer' }}
                      onClick={() => navigateToCDProfile(cd.id)}
                    >
                      {cd.img ? <img src={cd.img} alt={cd.name} className="w-100 h-100" style={{ objectFit: 'cover' }} /> : cd.name.charAt(0)}
                    </div>
                    
                    <div style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <h6 className="m-0 fw-bold text-white text-truncate cd-modal-name" style={{ fontSize: '0.95rem', cursor: 'pointer' }} onClick={() => navigateToCDProfile(cd.id)}>
                          {cd.name}
                        </h6>
                        {cd.specialization && (
                          <span className="px-2 py-0.5 rounded-pill fw-bold text-white d-inline-flex align-items-center flex-shrink-0 cd-modal-badge" style={{ background: '#E10600', fontSize: '0.65rem', letterSpacing: '0.3px' }}>
                            <Film size={10} className="me-1" color="#ffffff" /> {formatCamelCase(cd.specialization)}
                          </span>
                        )}
                      </div>
                      <small className="d-block text-truncate fw-medium text-secondary mt-0.5 cd-modal-agency" style={{ fontSize: '0.78rem' }}>
                        {cd.agency ? formatCamelCase(cd.agency) : 'Casting Agency'}
                      </small>
                    </div>
                  </div>

                  {/* COMPACT UNFOLLOW BUTTON */}
                  <button 
                    className="btn btn-danger btn-sm px-3 py-1 fw-bold rounded-pill shadow-sm flex-shrink-0 ms-auto cd-modal-unfollow-btn" 
                    style={{ background: '#E10600', color: '#ffffff', border: 'none', fontSize: '0.75rem', height: '28px', lineHeight: '1' }} 
                    onClick={() => handleModalFollowToggle(cd.id, cd.name)}
                  >
                    Unfollow
                  </button>
                </div>

                {/* BOTTOM ROW: 3 EQUAL STAT CARDS WITH CIRCULAR ICON BADGES */}
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-2 p-2 rounded-3 flex-grow-1 cd-stat-box" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', minHeight: '48px' }}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 cd-stat-icon-circle" style={{ width: '32px', height: '32px', border: '1.5px solid #0284c7', background: 'rgba(56, 189, 248, 0.1)' }}>
                      <Briefcase size={14} color="#38bdf8" />
                    </div>
                    <div>
                      <div className="fw-bold text-info cd-stat-num" style={{ fontSize: '0.88rem', lineHeight: '1' }}>{cd.totalRoles || 0}</div>
                      <div className="text-secondary cd-stat-lbl" style={{ fontSize: '0.68rem', fontWeight: '500', marginTop: '2px' }}>Roles</div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 p-2 rounded-3 flex-grow-1 cd-stat-box" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', minHeight: '48px' }}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 cd-stat-icon-circle" style={{ width: '32px', height: '32px', border: '1.5px solid #ca8a04', background: 'rgba(250, 204, 21, 0.1)' }}>
                      <Film size={14} color="#facc15" />
                    </div>
                    <div>
                      <div className="fw-bold text-warning cd-stat-num" style={{ fontSize: '0.88rem', lineHeight: '1' }}>{cd.totalProjects || 0}</div>
                      <div className="text-secondary cd-stat-lbl" style={{ fontSize: '0.68rem', fontWeight: '500', marginTop: '2px' }}>Projects</div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 p-2 rounded-3 flex-grow-1 cd-stat-box" style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.3)', minHeight: '48px' }}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 cd-stat-icon-circle" style={{ width: '32px', height: '32px', border: '1.5px solid #059669', background: 'rgba(16, 185, 129, 0.15)' }}>
                      <TrendingUp size={14} color="#10b981" />
                    </div>
                    <div>
                      <div className="fw-bold text-success cd-stat-num" style={{ fontSize: '0.88rem', lineHeight: '1' }}>{cd.successRate || '94%'}</div>
                      <div className="cd-stat-lbl" style={{ fontSize: '0.68rem', fontWeight: '500', color: '#9ca3af', marginTop: '2px' }}>Success</div>
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-muted">
            <Users size={28} className="mb-2 opacity-50" />
            <p className="mb-0 small">You are not following any Casting Directors yet.</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default FollowedCastingDirectorsModal;
